/**
 * `/settings/purchasing` — the purchasing configuration that used to live only behind buttons inside
 * operational pages (sidebar-nav audit F5): the reorder model (Inventory › On hand › "Reorder settings"),
 * FSP's ship-to address and the S&S payment method (both Inventory › Purchase draft › Purchase orders).
 * Those entry points stay. This page is the address an operator can be TOLD to go to — the ship station,
 * for one, refuses to buy a label until the ship-to address exists, because it doubles as the label's
 * ship-from; until now its refusal named a setting nobody could find.
 *
 * Three cards, each showing the CURRENT value and opening the EXISTING dialog. No new write path: the
 * dialogs own their forms, their gating (`isInventoryWrite`, cosmetic — the server 403s) and their
 * invalidation. The cards read the same query keys the dialogs do, so a save in a dialog refreshes its
 * card for free. Money-adjacent (these parameters drive purchasing dollars), not a money path.
 */
import { useQuery } from '@tanstack/react-query';
import { CreditCard, MapPin, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { PaymentProfileDialog } from './payment-profile-dialog';
import { ReorderConfigDialog } from './reorder-config-dialog';
import { ShipToConfigDialog } from './shipto-config-dialog';

type Editor = 'reorder' | 'shipto' | 'payment' | null;

export function SettingsPurchasingPage() {
  const client = useApiClient();
  const { state } = useAuth();
  // Cosmetic — the dialogs disable their forms for a viewer and the server 403s regardless.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [editor, setEditor] = useState<Editor>(null);
  const close = () => setEditor(null);

  // The SAME keys the dialogs use, so their post-save invalidation refreshes these cards.
  const reorder = useQuery({
    queryKey: ['reorder-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/reorder-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the reorder model.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const shipTo = useQuery({
    queryKey: ['shipto-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/shipto-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the ship-to address.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const payment = useQuery({
    queryKey: ['payment-profile-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/payment-profile-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the payment method.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const verb = canWrite ? 'Edit' : 'View';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Purchasing</h1>
        <p className="text-sm text-muted-foreground">
          How blanks get reordered and where S&amp;S ships and bills them. The same three settings
          are reachable from the Inventory pages; this is their home.
          {!canWrite && ' Your role can view these settings but not change them.'}
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <SettingCard
          icon={<SlidersHorizontal className="size-4" />}
          title="Reorder model"
          description="Lead time, service level and demand window behind every suggested reorder point and draft-PO quantity."
          action={
            <Button variant="outline" size="sm" onClick={() => setEditor('reorder')}>
              {verb}
            </Button>
          }
          state={reorder}
        >
          {(view) => (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Lead time</dt>
              <dd className="tabular-nums">{view.body.leadTimeDays} days</dd>
              <dt className="text-muted-foreground">Service level</dt>
              <dd className="tabular-nums">
                {view.body.serviceLabel}{' '}
                <span className="text-muted-foreground">(z {view.body.serviceZ})</span>
              </dd>
              <dt className="text-muted-foreground">Demand window</dt>
              <dd className="tabular-nums">{view.body.windowDays} days</dd>
              <Provenance updatedAt={view.updatedAt} updatedBy={view.updatedBy} />
            </dl>
          )}
        </SettingCard>

        <SettingCard
          icon={<MapPin className="size-4" />}
          title="Ship-to address"
          description="Where S&S delivers blank orders — and the ship-from printed on every carrier label the ship station buys."
          action={
            <Button variant="outline" size="sm" onClick={() => setEditor('shipto')}>
              {verb}
            </Button>
          }
          state={shipTo}
        >
          {(view) =>
            view.body ? (
              <div className="space-y-2 text-sm">
                <address className="not-italic leading-snug">
                  <div className="font-medium">{view.body.customer}</div>
                  {view.body.attn ? <div>Attn: {view.body.attn}</div> : null}
                  <div>{view.body.address}</div>
                  <div>
                    {view.body.city}, {view.body.state} {view.body.zip}
                  </div>
                </address>
                <p className="text-xs text-muted-foreground">
                  {view.body.residential ? 'Residential address' : 'Commercial address'}
                  {view.body.emailConfirmation
                    ? ` · order confirmations to ${view.body.emailConfirmation}`
                    : ''}
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 text-sm">
                  <Provenance updatedAt={view.updatedAt} updatedBy={view.updatedBy} />
                </dl>
              </div>
            ) : (
              <p className="text-sm text-destructive">
                Not set. S&amp;S orders cannot be placed and the ship station cannot buy a label
                until the shop’s address is here.
              </p>
            )
          }
        </SettingCard>

        <SettingCard
          icon={<CreditCard className="size-4" />}
          title="S&S payment method"
          description="The saved card or bank account on the S&S account that blank orders bill against."
          action={
            <Button variant="outline" size="sm" onClick={() => setEditor('payment')}>
              {canWrite ? 'Change' : 'View'}
            </Button>
          }
          state={payment}
        >
          {(view) =>
            view.body ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Method</dt>
                <dd>
                  {view.body.label ?? `Profile ${view.body.profileID}`}
                  {view.body.profileType ? (
                    <span className="text-muted-foreground"> · {view.body.profileType}</span>
                  ) : null}
                </dd>
                <dt className="text-muted-foreground">S&amp;S account</dt>
                <dd className="truncate">{view.body.email}</dd>
                <Provenance updatedAt={view.updatedAt} updatedBy={view.updatedBy} />
              </dl>
            ) : (
              <p className="text-sm text-destructive">
                Not set. S&amp;S rejects an order — even a test order — without a payment method.
              </p>
            )
          }
        </SettingCard>
      </div>

      <ReorderConfigDialog open={editor === 'reorder'} onClose={close} />
      <ShipToConfigDialog open={editor === 'shipto'} onClose={close} />
      <PaymentProfileDialog open={editor === 'payment'} onClose={close} />
    </div>
  );
}

/** Loading / error / value in one place, so the three cards read the same in every state. */
function SettingCard<T>({
  icon,
  title,
  description,
  action,
  state,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action: ReactNode;
  state: { data: T | undefined; isPending: boolean; error: unknown; refetch: () => unknown };
  children: (view: T) => ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
        <CardAction>{action}</CardAction>
      </CardHeader>
      <CardContent>
        {state.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : state.error ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <p className="text-destructive">
              {state.error instanceof Error ? state.error.message : 'Failed to load.'}
            </p>
            <Button variant="ghost" size="sm" onClick={() => state.refetch()}>
              Retry
            </Button>
          </div>
        ) : state.data !== undefined ? (
          children(state.data)
        ) : null}
      </CardContent>
    </Card>
  );
}

/** "Updated <date> by <who>" as the last row of a card's definition list; nothing when never saved. */
function Provenance({
  updatedAt,
  updatedBy,
}: {
  updatedAt: string | null;
  updatedBy: string | null;
}) {
  if (!updatedAt) return null;
  return (
    <>
      <dt className="text-muted-foreground">Updated</dt>
      <dd className="text-muted-foreground">
        {updatedAt.slice(0, 10)}
        {updatedBy ? ` by ${updatedBy}` : ''}
      </dd>
    </>
  );
}
